"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { CHAUFFEUR_SLUG_UI } from "./chauffeurSlugUi";
import { tr } from "@/config/localeText";

type GenericInfoProps = {
  heading: string;
  subheading?: string;
  paragraphs?: string[];
  bullets?: string[];
  imageSrc?: string;
  imageAlt?: string;
  locale?: Locale;
};

export const GenericInfo = ({
  heading,
  subheading,
  paragraphs = [],
  bullets = [],
  imageSrc,
  imageAlt,
  locale = "fr",
}: GenericInfoProps): JSX.Element => {
  const ui = tr(CHAUFFEUR_SLUG_UI, locale);
  const sectionBg = "bg-white text-black";
  const cardBg = "bg-black/[0.02]";
  const borderCol = "border-black/10";
  const subText = "text-black/75";

  return (
    <section className={`${sectionBg} py-16`}>
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="rounded-3xl border border-black/10 bg-black/[0.02] p-6 md:p-8"
        >
          <motion.p
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.4 }}
            className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
          >
            {ui.genericEyebrow}
          </motion.p>
          <div className="grid gap-6 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-6">
              <motion.h2
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.45, delay: 0.03 }}
                className="mb-2 font-space-age text-xl tracking-[0.2em]"
              >
                {heading}
              </motion.h2>
              {subheading && <p className={`mb-4 ${subText}`}>{subheading}</p>}
              {paragraphs.length > 0 && (
                <div className={`space-y-3 ${subText}`}>
                  {paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              )}
              {bullets.length > 0 && (
                <ul className="mt-5 grid gap-2 text-sm">
                  {bullets.map((b, idx) => (
                    <motion.li
                      key={b}
                      initial={false}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-10%" }}
                      transition={{ duration: 0.3, delay: idx * 0.03 }}
                      whileHover={{ x: 2 }}
                      className={`rounded-xl border border-black/10 bg-white px-4 py-3 ${subText}`}
                    >
                      {b}
                    </motion.li>
                  ))}
                </ul>
              )}
              <div className="mt-6">
                <Link
                  href={ui.reservationHref}
                  className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-black transition hover:bg-gold-600 hover:text-white"
                >
                  {ui.genericCtaLabel}
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div
                className={`relative overflow-hidden rounded-2xl border ${borderCol} ${cardBg}`}
              >
                {imageSrc ? (
                  <div className="relative h-64 w-full md:h-80">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt={imageAlt || heading}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-black/10" />
                  </div>
                ) : (
                  <div className={`p-6 ${subText}`}>
                    <p>{subheading || ui.genericFallback}</p>
                  </div>
                )}
                <div className="border-t border-black/10 bg-white px-5 py-4">
                  <p className="text-xs uppercase tracking-[0.15em] text-black/55">
                    {ui.genericFooter}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GenericInfo;
